import React from 'react';
import ReactDOM from 'react-dom';
import { DataProvider, useData, useField } from './EpcrDataContext';
import { Icons } from './EpcrIcons';
import { EpcrViewIncident } from './views/EpcrViewIncident';
import { EpcrViewPatient } from './views/EpcrViewPatient';
import { EpcrViewVitals } from './views/EpcrViewVitals';
import { EpcrViewFlowchart } from './views/EpcrViewFlowchart';
import { EpcrViewAssessments } from './views/EpcrViewAssessments';
import { EpcrViewNarrative } from './views/EpcrViewNarrative';
import { EpcrViewBilling, EpcrViewSignatures } from './views/EpcrViewRest';
import { EpcrViewRaw, RawField, groupLabel } from './views/EpcrViewRaw';
import { AutoField, Toast } from './EpcrFields';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import './EpcrPage.css';

const URLS = require('../../../urls.js');

const VIEWS = [
  { id: "incident",    label: "Incident",    icon: Icons.truck },
  { id: "patient",     label: "Patient",     icon: Icons.user },
  { id: "vitals",      label: "Vitals",      icon: Icons.pulse },
  { id: "flowchart",   label: "Flowchart",   icon: Icons.flow },
  { id: "assessments", label: "Assessments", icon: Icons.clipboard },
  { id: "narrative",   label: "Narrative",   icon: Icons.note },
  { id: "billing",     label: "Billing",     icon: Icons.dollar },
  { id: "signatures",  label: "Signatures",  icon: Icons.pen },
  { id: "raw",         label: "Raw",         icon: Icons.list },
];

const VIEW_COMPONENTS = {
  incident:    EpcrViewIncident,
  patient:     EpcrViewPatient,
  vitals:      EpcrViewVitals,
  flowchart:   EpcrViewFlowchart,
  assessments: EpcrViewAssessments,
  narrative:   EpcrViewNarrative,
  billing:     EpcrViewBilling,
  signatures:  EpcrViewSignatures,
  raw:         EpcrViewRaw,
};

// Maps NEMSIS element numbers to the view and section that contain them.
// Prefix-based fallback handles any element not explicitly listed.
const FIELD_MAP = {
  // incident — response
  'eRecord.01':    { view: 'incident', section: 'response' },
  'eResponse.03':  { view: 'incident', section: 'response' },
  'eResponse.04':  { view: 'incident', section: 'response' },
  'eResponse.05':  { view: 'incident', section: 'response' },
  'eResponse.07':  { view: 'incident', section: 'response' },
  'eResponse.08':  { view: 'incident', section: 'response' },
  'eResponse.09':  { view: 'incident', section: 'response' },
  'eResponse.13':  { view: 'incident', section: 'response' },
  'eResponse.14':  { view: 'incident', section: 'response' },
  'eResponse.23':  { view: 'incident', section: 'response' },
  'eResponse.24':  { view: 'incident', section: 'response' },
  // incident — scene
  'eScene.06':     { view: 'incident', section: 'scene' },
  'eScene.07':     { view: 'incident', section: 'scene' },
  'eScene.09':     { view: 'incident', section: 'scene' },
  'eScene.11':     { view: 'incident', section: 'scene' },
  'eScene.15':     { view: 'incident', section: 'scene' },
  'eScene.17':     { view: 'incident', section: 'scene' },
  'eScene.18':     { view: 'incident', section: 'scene' },
  'eScene.19':     { view: 'incident', section: 'scene' },
  'eScene.21':     { view: 'incident', section: 'scene' },
  // incident — times
  'eTimes.01':     { view: 'incident', section: 'times' },
  'eTimes.02':     { view: 'incident', section: 'times' },
  'eTimes.03':     { view: 'incident', section: 'times' },
  'eTimes.05':     { view: 'incident', section: 'times' },
  'eTimes.06':     { view: 'incident', section: 'times' },
  'eTimes.07':     { view: 'incident', section: 'times' },
  'eTimes.09':     { view: 'incident', section: 'times' },
  'eTimes.11':     { view: 'incident', section: 'times' },
  'eTimes.12':     { view: 'incident', section: 'times' },
  'eTimes.13':     { view: 'incident', section: 'times' },
  // incident — disposition
  'eDisposition.27': { view: 'incident', section: 'disposition' },
  'eDisposition.13': { view: 'incident', section: 'disposition' },
  'eDisposition.14': { view: 'incident', section: 'disposition' },
  'eDisposition.16': { view: 'incident', section: 'disposition' },
  'eDisposition.17': { view: 'incident', section: 'disposition' },
  'eDisposition.19': { view: 'incident', section: 'disposition' },
  'eDisposition.20': { view: 'incident', section: 'destination' },
  'eDisposition.21': { view: 'incident', section: 'destination' },
  'eDisposition.28': { view: 'incident', section: 'disposition' },
  'eDisposition.29': { view: 'incident', section: 'disposition' },
  'eDisposition.30': { view: 'incident', section: 'disposition' },
  'eDisposition.31': { view: 'incident', section: 'disposition' },
  'eDisposition.32': { view: 'incident', section: 'disposition' },
  // patient — demographics
  'ePatient.02':   { view: 'patient', section: 'demographics' },
  'ePatient.03':   { view: 'patient', section: 'demographics' },
  'ePatient.04':   { view: 'patient', section: 'demographics' },
  'ePatient.12':   { view: 'patient', section: 'demographics' },
  'ePatient.14':   { view: 'patient', section: 'demographics' },
  'ePatient.15':   { view: 'patient', section: 'demographics' },
  'ePatient.16':   { view: 'patient', section: 'demographics' },
  'ePatient.17':   { view: 'patient', section: 'demographics' },
  'ePatient.24':   { view: 'patient', section: 'demographics' },
  'ePatient.25':   { view: 'patient', section: 'demographics' },
  // patient — contact
  'ePatient.05':   { view: 'patient', section: 'contact' },
  'ePatient.06':   { view: 'patient', section: 'contact' },
  'ePatient.07':   { view: 'patient', section: 'contact' },
  'ePatient.08':   { view: 'patient', section: 'contact' },
  'ePatient.09':   { view: 'patient', section: 'contact' },
  'ePatient.10':   { view: 'patient', section: 'contact' },
  'ePatient.18':   { view: 'patient', section: 'contact' },
  'ePatient.19':   { view: 'patient', section: 'contact' },
  // patient — history / allergies
  'eHistory.06':   { view: 'patient', section: 'allergies' },
  'eHistory.07':   { view: 'patient', section: 'allergies' },
  'eHistory.08':   { view: 'patient', section: 'history' },
  'eHistory.13':   { view: 'patient', section: 'history' },
  // narrative
  'eSituation.01': { view: 'narrative', section: 'times' },
  'eSituation.03': { view: 'narrative', section: 'complaint' },
  'eSituation.04': { view: 'narrative', section: 'complaint' },
  'eSituation.05': { view: 'narrative', section: 'complaint' },
  'eSituation.06': { view: 'narrative', section: 'complaint' },
  'eSituation.07': { view: 'narrative', section: 'complaint' },
  'eSituation.09': { view: 'narrative', section: 'impression' },
  'eSituation.10': { view: 'narrative', section: 'signs' },
  'eSituation.11': { view: 'narrative', section: 'impression' },
  'eSituation.12': { view: 'narrative', section: 'impression' },
  'eSituation.18': { view: 'narrative', section: 'times' },
  'eNarrative.01': { view: 'narrative', section: 'narrative' },
  'eInjury.01':    { view: 'narrative', section: 'injuries' },
  'eInjury.03':    { view: 'narrative', section: 'injuries' },
};

const VIEW_BY_PREFIX = {
  eRecord: 'incident', eResponse: 'incident', eTimes: 'incident',
  eScene: 'incident', eDisposition: 'incident', eCrew: 'incident',
  ePatient: 'patient', eHistory: 'patient',
  eVitals: 'vitals',
  eProcedures: 'flowchart', eMedications: 'flowchart',
  eSituation: 'narrative', eNarrative: 'narrative', eInjury: 'narrative',
  eExam: 'assessments',
};

function getFieldLocation(elemNumber) {
  if (!elemNumber) return null;
  if (FIELD_MAP[elemNumber]) return FIELD_MAP[elemNumber];
  const prefix = elemNumber.split('.')[0];
  const view = VIEW_BY_PREFIX[prefix];
  return view ? { view, section: null } : null;
}

const REPORTS = [
  { file: "report_empty.json",       label: "2026-demo-01",   sublabel: "Blank Report" },
  { file: "report.json",             label: "2026-demo-02",   sublabel: "Seizure" },
];

// Hardware-device voice note recordings available to attach to a CADMUS send.
// Fetched on demand from ReactApp/public/epcr-data/voice_note_files/.
const VOICE_NOTE_FILES = ["voice_notes_seizure.json", "voice_notes_arrest.json"];

// Static demo audio (not pre-transcribed) served from
// ReactApp/public/epcr-data/voice_note_recordings/ — unlike VOICE_NOTE_FILES above, these need
// an explicit "Send to be transcribed" click (POST /api/admin/transcribe_voice_note_recording)
// before there's any text to attach.
const VOICE_NOTE_RECORDINGS = ["cardiac_arrest_bodycam.mp3", "asthma_attack_combined.mp3"];

export default function EpcrPage() {
  return (
    <DataProvider>
      <EpcrApp />
    </DataProvider>
  );
}

// ── 7A helpers: path resolution and op application ──────────────────────────

function mutationKey(r) {
  return `${r.feedbackTarget}:${r.elem}:${r.op}:${r.value}:${r.test_metadata_id}:${r.groupIndex ?? 'x'}`;
}

function promoteToMutation(proto, data) {
  const path = (proto.elem ? data.path(proto.elem) : null) || null;
  const groupPath = (path && path.includes('Group'))
    ? path.split('/').slice(0, -1).join('/')
    : null;

  // If the LLM supplied a groupIndex, inject it after the Group segment so the
  // write targets the correct array item rather than defaulting to [0].
  let resolvedPath = path;
  if (path && groupPath && Number.isInteger(proto.groupIndex) && proto.groupIndex >= 0) {
    const groupArr = readAtPath(data.report, groupPath);
    let idx = 0;
    if (Array.isArray(groupArr)) {
      if (proto.groupIndex < groupArr.length) {
        idx = proto.groupIndex;
      } else {
        console.warn(`[promoteToMutation] groupIndex=${proto.groupIndex} out of bounds (len=${groupArr.length}), falling back to 0`);
      }
    }
    const segs = path.split('/');
    const gi = segs.findIndex(s => s.includes('Group'));
    if (gi !== -1) {
      segs.splice(gi + 1, 0, String(idx));
      resolvedPath = segs.join('/');
    }
  }

  return { ...proto, path: resolvedPath, groupPath };
}

function readAtPath(report, path) {
  try {
    let obj = report.EMSDataSet.Header.PatientCareReport;
    const segs = path.split('/');
    for (let i = 0; i < segs.length; i++) {
      if (obj == null) return undefined;
      obj = obj[segs[i]];
      // Dereference repeating group arrays on intermediate segments only.
      // Skip when the next segment is already a numeric index (Raw view paths).
      // The final segment is left as-is so add_group receives the full array.
      if (i < segs.length - 1 && Array.isArray(obj) && !/^\d+$/.test(segs[i + 1])) obj = obj[0];
    }
    return obj;
  } catch { return undefined; }
}

function applyMutation(proto, data) {
  if (!proto.elem || !proto.op) return false;
  const m = promoteToMutation(proto, data);
  if (!m.path) {
    console.warn(`[applyMutation] no path for elem=${proto.elem}`);
    return false;
  }
  const { op, value, path, groupPath } = m;
  const current = readAtPath(data.report, path);

  switch (op) {
    case 'set':
    case 'clear':
      data.setReportPath(path, value ?? null);
      break;
    case 'replace':
      if (Array.isArray(current)) {
        data.setReportPath(path, current.map(v => v === proto.removeValue ? value : v));
      } else {
        data.setReportPath(path, value);
      }
      break;
    case 'add': {
      const arr = Array.isArray(current) ? current : (current != null ? [current] : []);
      data.setReportPath(path, [...arr, value]);
      break;
    }
    case 'add_group':
    case 'add_group_full': {
      // add_group_full carries a richer, multi-field proto.template (identity + anchor
      // time + whatever extra fields were determined — see MutationCard), but the write
      // itself is identical: append the template (or the single-field fallback) to the
      // group array.
      if (!groupPath) { console.warn(`[applyMutation] ${op} but no groupPath`); return false; }
      const groupCurrent = readAtPath(data.report, groupPath);
      const arr = Array.isArray(groupCurrent) ? groupCurrent : (groupCurrent != null ? [groupCurrent] : []);
      const newGroup = proto.template || (value != null ? { [proto.elem]: value } : {});
      data.setReportPath(groupPath, [...arr, newGroup]);
      break;
    }
    case 'ask_user':
      // Value is written live by RawField as the user fills in the field directly
      // inside the Suggested Edits card (see MutationCard) — nothing left to apply.
      break;
    default:
      console.warn(`[applyMutation] unknown op=${op}`);
      return false;
  }
  console.log(`[applyMutation] op=${op} elem=${proto.elem} path=${path} value=${value}`);
  return true;
}

function EpcrApp() {
  const [view, setView]                 = React.useState("incident");
  const [rapidOpen, setRapidOpen]       = React.useState(false);
  const [reportOpen, setReportOpen]     = React.useState(false);
  const [validationOpen, setValidationOpen] = React.useState(false);
  const [schematronOpen, setSchematronOpen] = React.useState(false);
  const [validating, setValidating]     = React.useState(false);
  const [violations, setViolations]     = React.useState(null);
  const [sending, setSending]                         = React.useState(false);
  const [dropdownResults, setDropdownResults]         = React.useState(null);
  const [schematronMutations, setSchematronMutations] = React.useState(null);
  const [rejectedIds, setRejectedIds]                 = React.useState([]);
  const [acceptedIds, setAcceptedIds]                 = React.useState([]);
  const [voiceNoteCache, setVoiceNoteCache]           = React.useState({});
  const [attachedNotes, setAttachedNotes]             = React.useState([]);
  const [appNoteBatches, setAppNoteBatches]           = React.useState([]);
  // Result of the most recent "Process with AWS Transcribe Medical" click — deliberately not
  // history, just whatever was processed last (see the "Latest Transcription Comparison" tab).
  const [latestComparison, setLatestComparison]       = React.useState(null);
  const data = useData();

  const handleSelectReport = React.useCallback(async (file) => {
    setReportOpen(false);
    setValidationOpen(false);
    setSchematronOpen(false);
    setViolations(null);
    setValidating(false);
    setSending(false);
    setDropdownResults(null);
    setSchematronMutations(null);
    setRejectedIds([]);
    setAcceptedIds([]);
    setAttachedNotes([]);
    await data.loadReport(file);
  }, [data]);

  const loadVoiceNoteFile = React.useCallback(async (file) => {
    if (voiceNoteCache[file]) return voiceNoteCache[file];
    try {
      const json = await fetch(`/epcr-data/voice_note_files/${file}`).then(r => r.json());
      const recordings = json.recordings || [];
      setVoiceNoteCache(c => ({ ...c, [file]: recordings }));
      return recordings;
    } catch (e) {
      console.error('Voice note file load error:', e);
      setVoiceNoteCache(c => ({ ...c, [file]: [] }));
      return [];
    }
  }, [voiceNoteCache]);

  const toggleAttachNote = React.useCallback((note, file) => {
    const uid = `${file}:${note.id}`;
    setAttachedNotes(prev => {
      const exists = prev.some(n => n.uid === uid);
      if (exists) return prev.filter(n => n.uid !== uid);
      return [...prev, { uid, text: note.text, timestamp: note.timestamp, source: file }];
    });
  }, []);

  const attachAllFromFile = React.useCallback((file, recordings) => {
    setAttachedNotes(prev => {
      const existingUids = new Set(prev.map(n => n.uid));
      const toAdd = (recordings || [])
        .filter(rec => !existingUids.has(`${file}:${rec.id}`))
        .map(rec => ({ uid: `${file}:${rec.id}`, text: rec.text, timestamp: rec.timestamp, source: file }));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  }, []);

  const addManualNote = React.useCallback((text, timestamp) => {
    const uid = `manual:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
    setAttachedNotes(prev => [...prev, { uid, text, timestamp, source: 'manual' }]);
  }, []);

  // Polls the batches the mvp3 Android app has POSTed to /api/admin/receive_notes_from_app
  // (in-memory on the backend, not persisted). Called on mount of VoiceNotesPanel and on an
  // interval while that tab is visible, plus a manual "Refresh" button — see VoiceNotesPanel.
  const refreshAppNotes = React.useCallback(async () => {
    try {
      const resp = await fetch(URLS.LIST_NOTES_FROM_APP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const result = await resp.json();
      setAppNoteBatches(result.batches || []);
    } catch (e) {
      console.error('List notes from app error:', e);
    }
  }, []);

  const removeAttachedNote = React.useCallback((uid) => {
    setAttachedNotes(prev => prev.filter(n => n.uid !== uid));
  }, []);

  const clearAttachedNotes = React.useCallback(() => {
    setAttachedNotes([]);
  }, []);

  const runValidation = React.useCallback(async () => {
    if (!data?.report) return;
    setValidating(true);
    setSchematronOpen(true);
    try {
      const resp = await fetch(URLS.VALIDATE_EPCR_DRAFT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ json: data.report }),
      });
      const result = await resp.json();
      setViolations(result.violations || []);
    } catch (e) {
      console.error('Validation error:', e);
      setViolations([]);
    } finally {
      setValidating(false);
    }
  }, [data]);

  const runSendToCadmus = React.useCallback(async () => {
    if (!data?.report) return;
    setSending(true);
    setValidationOpen(true);
    try {
      // Sequential per-send id — this is what the backend hands to the LLM as a
      // timestamp proxy (see validateClinicalDomainsWithNarrativeAndVoiceNotes),
      // so the LLM never sees the real timestamp, only the id and text.
      const notes = attachedNotes.map((n, i) => ({ id: i + 1, timestamp: n.timestamp, text: n.text }));
      const resp = await fetch(URLS.SEND_TO_CADMUS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ json: data.report, notes }),
      });
      const result = await resp.json();
      setViolations(result.violations || []);
      setDropdownResults(result.dropdownResults || []);
      setSchematronMutations(result.schematronMutations || []);
    } catch (e) {
      console.error('Send to CADMUS error:', e);
      setDropdownResults([]);
      setSchematronMutations([]);
    } finally {
      setSending(false);
    }
  }, [data, attachedNotes]);

  const handleAccept = React.useCallback((r) => {
    applyMutation(r, data);
    setAcceptedIds(ids => [...ids, mutationKey(r)]);
  }, [data]);

  const handleReject = React.useCallback((r) => {
    setRejectedIds(ids => [...ids, mutationKey(r)]);
  }, []);

  const handleAcceptAll = React.useCallback(() => {
    const all = [
      ...(schematronMutations || []).filter(r => r.severity !== '[FATAL]'),
      ...(dropdownResults || []),
    ];
    const rejectedSet = new Set(rejectedIds);
    const toApply = all.filter(r => r.op !== null && r.op !== 'ask_user' && !rejectedSet.has(mutationKey(r)));
    const newlyAccepted = [];
    for (const r of toApply) {
      applyMutation(r, data);
      newlyAccepted.push(mutationKey(r));
    }
    if (newlyAccepted.length > 0) {
      setAcceptedIds(ids => [...ids, ...newlyAccepted]);
    }
  }, [schematronMutations, dropdownResults, rejectedIds, data]);

  const navigateToViolation = React.useCallback((violation) => {
    // For schematron violations, contextLocalName is the exact XML element that fired —
    // derive the tab from its prefix. Fall back to static FIELD_MAP for clinical mutations.
    const view = (() => {
      if (violation.contextLocalName) {
        const prefix = violation.contextLocalName.split('.')[0];
        return VIEW_BY_PREFIX[prefix] || null;
      }
      return getFieldLocation(violation.element)?.view || null;
    })();
    if (view) setView(view);
    setSchematronOpen(false);
    const loc = getFieldLocation(violation.element);
    setTimeout(() => {
      const flash = (el) => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('epcr-jump-flash');
        setTimeout(() => el.classList.remove('epcr-jump-flash'), 1500);
      };
      // Prefer card-level targeting when the violation carries DOM-derived group info.
      const groupPrefix = violation.contextLocalName?.split('.')[0];
      const groupIndex  = violation.contextGroupIndex;
      const cardEl = (groupPrefix != null && groupIndex != null)
        ? document.querySelector(`[data-group-prefix="${groupPrefix}"][data-group-index="${groupIndex}"]`)
        : null;
      if (cardEl) {
        flash(cardEl);
      } else {
        const fieldEl = document.getElementById(`field-${violation.element}`);
        if (fieldEl) {
          flash(fieldEl);
        } else if (loc?.section) {
          const sectionEl = document.querySelector(`[data-section="${loc.section}"]`);
          if (sectionEl) sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, 80);
  }, []);

  if (!data?.loaded) {
    return (
      <div className="epcr-root" style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14, color: "var(--epcr-ink-3)" }}>
        <div className="loader-brand"><span className="r">CADMUS</span> ePCR</div>
        <div className="loader-bar" />
        <div style={{ fontSize: 12 }}>Loading enumerations & defined lists…</div>
      </div>
    );
  }

  const ViewComponent = VIEW_COMPONENTS[view];
  const totalCount = violations ? violations.length : 0;

  return (
    <div className="epcr-root">
      <TopToolbar
        onRapid={() => setRapidOpen(o => !o)}
        rapidOpen={rapidOpen}
        onCloseRapid={() => setRapidOpen(false)}
        onOpenValidationMenu={() => setValidationOpen(true)}
        onValidate={runValidation}
        validating={validating}
        reports={REPORTS}
        currentReport={data.currentReport}
        reportOpen={reportOpen}
        onToggleReport={() => setReportOpen(o => !o)}
        onCloseReport={() => setReportOpen(false)}
        onSelectReport={handleSelectReport}
      />
      <ViewTabs current={view} onSelect={setView} />
      {/* attachedNotes: only the Incident (Update CAD) and Vitals (Pull from
          Monitor) views read this; every other view ignores the extra prop. */}
      <ViewComponent attachedNotes={attachedNotes} />
      {validationOpen && (
        <ValidationPanel
          violations={violations}
          validating={validating}
          onClose={() => setValidationOpen(false)}
          sending={sending}
          dropdownResults={dropdownResults}
          schematronMutations={schematronMutations}
          rejectedIds={rejectedIds}
          acceptedIds={acceptedIds}
          onAccept={handleAccept}
          onReject={handleReject}
          onAcceptAll={handleAcceptAll}
          onSendToCadmus={runSendToCadmus}
          voiceNoteFiles={VOICE_NOTE_FILES}
          voiceNoteCache={voiceNoteCache}
          onLoadVoiceNoteFile={loadVoiceNoteFile}
          attachedNotes={attachedNotes}
          onToggleAttachNote={toggleAttachNote}
          onAttachAllFromFile={attachAllFromFile}
          onAddManualNote={addManualNote}
          onRemoveAttachedNote={removeAttachedNote}
          onClearAttachedNotes={clearAttachedNotes}
          appNoteBatches={appNoteBatches}
          onRefreshAppNotes={refreshAppNotes}
          latestComparison={latestComparison}
          onSetLatestComparison={setLatestComparison}
        />
      )}
      {schematronOpen && (
        <SchematronPanel
          violations={violations}
          validating={validating}
          totalCount={totalCount}
          onClose={() => setSchematronOpen(false)}
          onRerun={runValidation}
          onNavigate={navigateToViolation}
          specFn={data.spec}
        />
      )}
    </div>
  );
}

function TopToolbar({ onRapid, rapidOpen, onCloseRapid, onOpenValidationMenu, onValidate, validating,
                      reports, currentReport, reportOpen, onToggleReport, onCloseReport, onSelectReport }) {
  const last = useField("ePatient/ePatient.PatientNameGroup/ePatient.02");
  const first = useField("ePatient/ePatient.PatientNameGroup/ePatient.03");
  const runNumber = useField("eResponse/eResponse.04");
  const incidentNumber = useField("eResponse/eResponse.03");
  const patientName = [first, last].filter(Boolean).join(" ") || "Unnamed Patient";
  const runLabel = runNumber || incidentNumber || "";

  return (
    <div className="t1">
      <div className="t1-brand">
        <span className="dot" />
        Example ePCR
      </div>
      <div style={{ position: "relative" }}>
        <button className="t1-patient" onClick={onToggleReport}>
          <span className="label">{runLabel ? "Run " + runLabel : ""}</span>
          <span className="name">
            {patientName} <Icons.chev className="chev" size={28} />
          </span>
        </button>
        {reportOpen && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 49 }} onClick={onCloseReport} />
            <div className="report-pop">
              <h4>Switch Report</h4>
              {reports.map(r => (
                <button
                  key={r.file}
                  className={r.file === currentReport ? "active" : ""}
                  onClick={() => onSelectReport(r.file)}
                >
                  <span className="report-pop-label">{r.label}</span>
                  <span className="report-pop-sub">{r.sublabel}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      {/* <button className="t1-btn" title="Switch report"><Icons.swap size={32} /></button> */}
      <div className="t1-spacer" />
      <div className="t1-actions" style={{ position: "relative" }}>
        {/* <button className="t1-btn" title="Keyboard shortcuts"><Icons.keyboard size={36} /></button> */}
        <button className="t1-btn" title="Validation menu" onClick={onOpenValidationMenu}>
          <Icons.rainbowStar size={40} />
        </button>
        <button
          className={"t1-btn" + (validating ? " rapid" : "")}
          title="Run schematron validation"
          onClick={onValidate}
        >
          <Icons.checkCircle size={40} />
        </button>
        {/* <button className="t1-btn" title="Capture image"><Icons.camera size={36} /></button> */}
        {/* <button className="t1-btn rapid" title="Rapid actions" onClick={onRapid}>
          <Icons.bolt size={36} />
        </button> */}
        {rapidOpen && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 49 }} onClick={onCloseRapid} />
            <div className="rapid-pop">
              <h4>Rapid actions</h4>
              <button><Icons.pulse size={28} /> Add vitals</button>
              <button><Icons.flow size={28} /> Repeat last treatment</button>
              <button><Icons.clipboard size={28} /> Reassessment template</button>
              <button><Icons.note size={28} /> Append to narrative</button>
              <button><Icons.alert size={28} /> Flag critical</button>
              <button className="danger"><Icons.alert size={28} /> Mark refused care</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ViewTabs({ current, onSelect }) {
  return (
    <nav className="t2" aria-label="Report sections">
      {VIEWS.map(v => (
        <button
          key={v.id}
          className={"t2-tab" + (current === v.id ? " active" : "")}
          onClick={() => onSelect(v.id)}
        >
          <v.icon size={32} className="icon" />
          <span>{v.label}</span>
        </button>
      ))}
    </nav>
  );
}

function ValidationPanel({ violations, validating, onClose,
                            sending, dropdownResults, schematronMutations,
                            rejectedIds, acceptedIds, onAccept, onReject, onAcceptAll, onSendToCadmus,
                            voiceNoteFiles, voiceNoteCache, onLoadVoiceNoteFile,
                            attachedNotes, onToggleAttachNote, onAttachAllFromFile,
                            onAddManualNote, onRemoveAttachedNote, onClearAttachedNotes,
                            appNoteBatches, onRefreshAppNotes,
                            latestComparison, onSetLatestComparison }) {
  const [tab, setTab] = React.useState('voicenotes');

  const data = useData();
  const debugMode = data.debugMode;

  // The Debug tab (and its button) disappear the instant debug mode turns
  // off — if it was the active tab, fall back rather than leaving the panel
  // stuck rendering a tab with no visible button to get back to it.
  React.useEffect(() => {
    if (!debugMode && tab === 'debug') setTab('voicenotes');
  }, [debugMode, tab]);

  // Per-batch "Process with AWS Transcribe Medical" — lives here (not in VoiceNotesPanel) so it
  // can auto-switch tabs on success, same pattern as onSendToCadmus below.
  const [processingBatchId, setProcessingBatchId] = React.useState(null);
  const [processError, setProcessError]           = React.useState(null);

  const handleProcessBatch = React.useCallback(async (batchId) => {
    setProcessingBatchId(batchId);
    setProcessError(null);
    try {
      const resp = await fetch(URLS.PROCESS_APP_BATCH_AUDIO_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchId }),
      });
      const result = await resp.json();
      if (result.status === 'success') {
        onSetLatestComparison(result);
        setTab('debug'); // Transcription Comparison now lives inside the Debug tab
      } else {
        setProcessError(result.message || 'Processing failed.');
      }
    } catch (e) {
      console.error('Process app batch audio error:', e);
      setProcessError('Processing request failed.');
    } finally {
      setProcessingBatchId(null);
    }
  }, [onSetLatestComparison]);

  // "Send to be transcribed" for the Voice Notes tab's static demo recordings — lives here for
  // the same reason as handleProcessBatch above (needs setTab in scope to jump straight to the
  // comparison view once transcription finishes). Unlike a phone batch, there's no "phone" side
  // to compare against and no persisted audio to play back afterward (both get deleted right
  // after transcription — see transcribeStandaloneRecording), so audioAvailable: false tells
  // TranscriptionComparisonPanel to hide the "Play audio" button for this result.
  const [transcribingRecording, setTranscribingRecording]   = React.useState(null);
  const [recordingTranscribeError, setRecordingTranscribeError] = React.useState(null);

  const handleTranscribeRecording = React.useCallback(async (filename) => {
    setTranscribingRecording(filename);
    setRecordingTranscribeError(null);
    try {
      const audioResp = await fetch(`/epcr-data/voice_note_recordings/${filename}`);
      const blob = await audioResp.blob();
      const audioBase64 = await blobToBase64(blob);
      const resp = await fetch(URLS.TRANSCRIBE_VOICE_NOTE_RECORDING_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audioBase64, mimeType: blob.type || 'audio/mpeg' }),
      });
      const result = await resp.json();
      if (result.status === 'success') {
        onSetLatestComparison({
          batchId: filename,
          phoneNotes: [],
          awsNotes: result.recordings,
          audioTruncated: false,
          audioAvailable: false,
        });
        setTab('debug'); // Transcription Comparison now lives inside the Debug tab
      } else {
        setRecordingTranscribeError(result.message || 'Transcription failed.');
      }
    } catch (e) {
      console.error('Transcribe recording error:', e);
      setRecordingTranscribeError('Transcription request failed.');
    } finally {
      setTranscribingRecording(null);
    }
  }, [onSetLatestComparison]);

  const rejectedSet = React.useMemo(() => new Set(rejectedIds || []), [rejectedIds]);
  const acceptedSet = React.useMemo(() => new Set(acceptedIds || []), [acceptedIds]);
  const visibleSchematron = React.useMemo(
    () => (schematronMutations || []).filter(r => !rejectedSet.has(mutationKey(r))),
    [schematronMutations, rejectedSet]
  );
  const visibleClinical = React.useMemo(
    () => (dropdownResults || []).filter(r => !rejectedSet.has(mutationKey(r))),
    [dropdownResults, rejectedSet]
  );
  const actionableCount = React.useMemo(
    () => [...visibleSchematron, ...visibleClinical]
            .filter(r => r.op !== null && r.op !== 'ask_user' && r.severity !== '[FATAL]').length,
    [visibleSchematron, visibleClinical]
  );
  // "Gone through" every suggested edit means every non-rejected item has also been
  // accepted (see MutationCard's isAccepted collapse) — including the trivial case of
  // there being none to begin with, which is what unlocks "Generate Narrative Draft".
  const remainingCount = React.useMemo(
    () => [...visibleSchematron, ...visibleClinical].filter(r => !acceptedSet.has(mutationKey(r))).length,
    [visibleSchematron, visibleClinical, acceptedSet]
  );

  // Actionable (one-click Accept) cards surface before "Needs Input" (ask_user, requires
  // typing/picking a value first) ones, across both sources — most visible outside debug
  // mode, where the Schematron/Clinical Analysis banners below are hidden and the two
  // sources already read as one flat list.
  const actionableSchematron = React.useMemo(() => visibleSchematron.filter(r => r.op !== 'ask_user'), [visibleSchematron]);
  const needsInputSchematron = React.useMemo(() => visibleSchematron.filter(r => r.op === 'ask_user'), [visibleSchematron]);
  const actionableClinical   = React.useMemo(() => visibleClinical.filter(r => r.op !== 'ask_user'), [visibleClinical]);
  const needsInputClinical   = React.useMemo(() => visibleClinical.filter(r => r.op === 'ask_user'), [visibleClinical]);

  return ReactDOM.createPortal(
    <div className="epcr-vp-back" onClick={onClose}>
      <div className="epcr-vp" onClick={e => e.stopPropagation()}>
        <div className="epcr-vp-head">
          {/* Triple left-click toggles debug mode — MouseEvent.detail is the browser's own
              consecutive-click counter, so no manual timing/counting is needed. Deliberately
              no visual affordance; this is a hidden dev toggle, not a discoverable control. */}
          <img
            src="/logo-dark.png"
            alt="CADMUS"
            style={{ height: 28, display: "block" }}
            onClick={e => { if (e.detail === 3) data.toggleDebugMode(); }}
          />
          <div className="epcr-vp-actions">
            <button
              className="epcr-vp-send"
              onClick={e => { e.stopPropagation(); onSendToCadmus(); setTab('mutations'); }}
              disabled={validating || sending}
            >
              {sending ? "Sending…" : "Send to CADMUS"}
            </button>
          </div>
          <button className="epcr-vp-close" onClick={onClose}>
            <Icons.close size={32} />
          </button>
        </div>

        <div className="epcr-vp-tabs">
          <button className={"epcr-vp-tab" + (tab === 'voicenotes' ? " active" : "")} onClick={() => setTab('voicenotes')}>
            Voice Notes
            {attachedNotes.length > 0 && (
              <span className="epcr-vp-tab-count">{attachedNotes.length}</span>
            )}
          </button>
          <button className={"epcr-vp-tab" + (tab === 'mutations' ? " active" : "")} onClick={() => setTab('mutations')}>
            Suggested Edits
            {(() => {
              // Rejected items are filtered out of visibleSchematron/visibleClinical, so this
              // count already excludes any current suggestion the user has dismissed — the
              // badge should never count something no longer shown anywhere in the tab.
              const total = visibleSchematron.length + visibleClinical.length;
              return (dropdownResults !== null || schematronMutations !== null) && total > 0
                ? <span className="epcr-vp-tab-count">{total}</span>
                : null;
            })()}
          </button>
          <button className={"epcr-vp-tab" + (tab === 'collab' ? " active" : "")} onClick={() => setTab('collab')}>
            Collaborative Narrative
          </button>
          {debugMode && (
            <button className={"epcr-vp-tab" + (tab === 'debug' ? " active" : "")} onClick={() => setTab('debug')}>
              Debug
            </button>
          )}
        </div>

        <div className="epcr-vp-list">
          {tab === 'raw' && (
            <>
              {sending && <div className="epcr-vp-empty">Sending to CADMUS — running full analysis…</div>}
              {!sending && dropdownResults === null && (
                <div className="epcr-vp-empty">Click "Send to CADMUS" to run full clinical analysis.</div>
              )}
              {!sending && dropdownResults !== null && (
                <>
                  {violations !== null && violations.length > 0 && (
                    <>
                      <div className="epcr-vp-group-head">Schematron ({violations.length})</div>
                      {violations.map((v, i) => (
                        <div key={i} className="epcr-vp-item" style={{ cursor: 'default' }}>
                          <div className="epcr-vp-item-meta">
                            <span className={"epcr-vp-badge " + (v.role === '[FATAL]' ? "fatal" : "warn")}>
                              {v.role === '[FATAL]' ? "Fatal" : "Warning"}
                            </span>
                            {v.element && <span className="epcr-vp-elem">{v.element}</span>}
                          </div>
                          <div className="epcr-vp-msg">{v.message}</div>
                        </div>
                      ))}
                    </>
                  )}
                  {violations !== null && violations.length === 0 && (
                    <div className="epcr-vp-group-head" style={{ color: '#16a34a' }}>Schematron — No violations</div>
                  )}
                  <div className="epcr-vp-group-head">AI Analysis ({dropdownResults.length})</div>
                  {dropdownResults.length === 0 && (
                    <div className="epcr-vp-empty" style={{ padding: '20px 36px' }}>No discrepancies found.</div>
                  )}
                  {dropdownResults.map((r, i) => (
                    <DropdownResultItem key={i} r={r} />
                  ))}
                </>
              )}
            </>
          )}

          {tab === 'mutations' && (
            <>
              {sending && <div className="epcr-vp-empty">Sending to CADMUS — running analysis…</div>}
              {!sending && dropdownResults === null && schematronMutations === null && (
                <div className="epcr-vp-empty">Click "Send to CADMUS" to generate mutations.</div>
              )}
              {!sending && (dropdownResults !== null || schematronMutations !== null) && (
                <>
                  {actionableCount > 0 && (
                    <div className="epcr-mut-bulk">
                      <span>{actionableCount} actionable edit{actionableCount !== 1 ? 's' : ''}</span>
                      <button className="epcr-mut-btn accept" onClick={onAcceptAll}>
                        Accept all edits
                      </button>
                    </div>
                  )}
                  {/* Source breakdown — debug info only; the cards themselves render below as
                      one actionable-first list regardless of which source produced them. */}
                  {debugMode && visibleSchematron.length > 0 && (
                    <div className="epcr-vp-group-head">Schematron Mutations ({visibleSchematron.length})</div>
                  )}
                  {debugMode && visibleClinical.length > 0 && (
                    <div className="epcr-vp-group-head">Clinical Analysis ({visibleClinical.length})</div>
                  )}
                  {[...actionableSchematron, ...actionableClinical].map(r => (
                    <MutationCard key={mutationKey(r)} r={r} isAccepted={acceptedSet.has(mutationKey(r))} onAccept={onAccept} onReject={onReject} />
                  ))}
                  {[...needsInputSchematron, ...needsInputClinical].map(r => (
                    <MutationCard key={mutationKey(r)} r={r} isAccepted={acceptedSet.has(mutationKey(r))} onAccept={onAccept} onReject={onReject} />
                  ))}
                  {visibleSchematron.length === 0 && visibleClinical.length === 0 && (
                    <div className="epcr-vp-empty">No mutations identified.</div>
                  )}
                  {remainingCount === 0 && (
                    <div className="epcr-mut-done">
                      <div className="epcr-mut-done-text">
                        {(visibleSchematron.length === 0 && visibleClinical.length === 0)
                          ? "No suggested edits for this report."
                          : "All suggested edits have been reviewed."}
                      </div>
                      <button className="epcr-vp-send" onClick={() => setTab('collab')}>
                        Generate Narrative Draft
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {tab === 'voicenotes' && (
            <VoiceNotesPanel
              files={voiceNoteFiles}
              cache={voiceNoteCache}
              onLoadFile={onLoadVoiceNoteFile}
              attachedNotes={attachedNotes}
              onToggleAttach={onToggleAttachNote}
              onAttachAllFromFile={onAttachAllFromFile}
              onAddManual={onAddManualNote}
              onRemoveAttached={onRemoveAttachedNote}
              onClearAttached={onClearAttachedNotes}
              appBatches={appNoteBatches}
              onRefreshAppNotes={onRefreshAppNotes}
              onProcessBatch={handleProcessBatch}
              processingBatchId={processingBatchId}
              processError={processError}
              onTranscribeRecording={handleTranscribeRecording}
              transcribingRecording={transcribingRecording}
              recordingTranscribeError={recordingTranscribeError}
            />
          )}

          {tab === 'collab' && (
            <CollaborativeNarrativePanel attachedNotes={attachedNotes} />
          )}

          {tab === 'debug' && debugMode && (() => {
            const currentKeys = [
              ...(schematronMutations || []),
              ...(dropdownResults || []),
            ].map(r => mutationKey(r));
            const sections = [
              { label: 'Current mutation keys', items: currentKeys },
              { label: 'acceptedIds', items: acceptedIds || [] },
              { label: 'rejectedIds', items: rejectedIds || [] },
            ];
            return (
              <>
                <div className="epcr-vp-group-head">Latest Transcription Comparison</div>
                <TranscriptionComparisonPanel comparison={latestComparison} onAttachAllFromFile={onAttachAllFromFile} />
                <div style={{ padding: '16px 20px', fontFamily: 'monospace', fontSize: 12 }}>
                  {sections.map(({ label, items }) => (
                    <div key={label} style={{ marginBottom: 20 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>{label} ({items.length})</div>
                      {items.length === 0
                        ? <div style={{ color: 'var(--epcr-muted, #888)' }}>—</div>
                        : items.map((k, i) => (
                            <div key={i} style={{ padding: '3px 0', borderBottom: '1px solid var(--epcr-border, #e5e7eb)', wordBreak: 'break-all' }}>{k}</div>
                          ))
                      }
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>,
    document.body
  );
}

const NARRATIVE_FORMATS = [
  { key: 'chart',         label: 'CHART',         hint: 'Chief Complaint · History · Assessment · Rx/Treatment · Transport' },
  { key: 'soap',          label: 'SOAP',          hint: 'Subjective · Objective · Assessment · Plan' },
  { key: 'chronological', label: 'Chronological', hint: 'Plain-language, time-ordered account of the call' },
];

// Collaborative Narrative tab — reached either directly or via "Generate Narrative
// Draft" once Suggested Edits has been fully reviewed (see the 'mutations' tab above).
// Nothing is written to eNarrative.01 until the user presses "Accept Narrative".
function CollaborativeNarrativePanel({ attachedNotes }) {
  const data = useData();
  const narrativePath = data.path('eNarrative.01') || 'eNarrative/eNarrative.01';
  const [allowDouble, setAllowDouble] = React.useState(false);
  const [format, setFormat] = React.useState('chronological');
  const [draft, setDraft] = React.useState('');
  const [generating, setGenerating] = React.useState(false);
  const [genError, setGenError] = React.useState(null);
  const [toast, setToast] = React.useState(null);

  // Narrative Candidates — the demo-side mirror of the real MedicReportFeedbackModule
  // flow (candidates pushed by report_tests/*.js, validated against the narrative by
  // validateNarrativeCandidates, then incorporated via editNarrative once the user supplies
  // medic_text per candidate they want addressed). candidates: null = not checked yet for
  // this draft, [] = checked, nothing to flag.
  const [candidates, setCandidates] = React.useState(null);
  const [candidatesLoading, setCandidatesLoading] = React.useState(false);
  const [candidatesError, setCandidatesError] = React.useState(null);
  const [medicTextByNumber, setMedicTextByNumber] = React.useState({});
  const [applying, setApplying] = React.useState(false);
  const [applyError, setApplyError] = React.useState(null);
  // Snapshot of the draft immediately before the last successful apply, so the diff
  // viewer below has a stable "before" to compare the current draft against.
  const [preApplyNarrative, setPreApplyNarrative] = React.useState(null);
  const [showDiff, setShowDiff] = React.useState(false);

  // Runs the same report-type-driven candidate checks the real ingestion pipeline runs at
  // save time, against the narrative text just produced — see generateNarrativeCandidates
  // in report-controller.js.
  const checkCandidates = async (narrativeText) => {
    setCandidatesLoading(true);
    setCandidatesError(null);
    setCandidates(null);
    setMedicTextByNumber({});
    try {
      const resp = await fetch(URLS.GENERATE_NARRATIVE_CANDIDATES, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ json: data.report, narrative: narrativeText }),
      });
      const result = await resp.json();
      if (result.candidates) {
        setCandidates(result.candidates);
      } else {
        setCandidatesError(result.error || 'Could not check narrative completeness.');
      }
    } catch (e) {
      console.error('Generate narrative candidates error:', e);
      setCandidatesError('Narrative check request failed.');
    } finally {
      setCandidatesLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenError(null);
    setPreApplyNarrative(null);
    setShowDiff(false);
    try {
      const notes = (attachedNotes || []).map(n => ({ timestamp: n.timestamp, text: n.text }));
      const resp = await fetch(URLS.GENERATE_NARRATIVE_DRAFT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ json: data.report, notes, format, allowDoubleDocumentation: allowDouble }),
      });
      const result = await resp.json();
      if (result.narrative) {
        setDraft(result.narrative);
        checkCandidates(result.narrative);
      } else {
        setGenError(result.error || 'Draft generation failed.');
      }
    } catch (e) {
      console.error('Generate narrative draft error:', e);
      setGenError('Draft generation request failed.');
    } finally {
      setGenerating(false);
    }
  };

  const handleApplyCandidates = async () => {
    const toApply = (candidates || [])
      .map(c => ({ ...c, medicText: (medicTextByNumber[c.suggestionNumber] || '').trim() }))
      .filter(c => c.medicText);
    if (toApply.length === 0) return;

    setApplying(true);
    setApplyError(null);
    try {
      const resp = await fetch(URLS.APPLY_NARRATIVE_CANDIDATES, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ narrative: draft, candidates: toApply }),
      });
      const result = await resp.json();
      if (result.narrative) {
        setPreApplyNarrative(draft);
        setDraft(result.narrative);
        setShowDiff(true);
        setCandidates(null);
        setMedicTextByNumber({});
      } else {
        setApplyError(result.error || 'Could not apply changes to the narrative.');
      }
    } catch (e) {
      console.error('Apply narrative candidates error:', e);
      setApplyError('Apply request failed.');
    } finally {
      setApplying(false);
    }
  };

  const handleAccept = () => {
    data.setReportPath(narrativePath, draft);
    setToast('Narrative accepted.');
  };

  return (
    // Inline, not the (otherwise-identical) .epcr-cn-root CSS rule — that selector
    // mysteriously never makes it into the parsed stylesheet in dev (confirmed via
    // DevTools Protocol: the rule's own text is present in the injected <style> tag,
    // every other .epcr-cn-* selector around it parses fine, but this one alone is
    // absent from document.styleSheets[...].cssRules every time, package/build-tool
    // quirk rather than anything wrong with the selector or JSX). Inline sidesteps it.
    <div
      className="epcr-cn-root"
      style={{ boxSizing: 'border-box', maxWidth: 760, margin: '0 auto', padding: 36, display: 'flex', flexDirection: 'column', gap: 26 }}
    >
      <div className="epcr-cn-opts">
        <div className="epcr-cn-opt-group">
          <div className="epcr-cn-opt-label">Double documentation</div>
          <div className="epcr-cn-toggle-row">
            <button className={"epcr-cn-toggle" + (!allowDouble ? " active" : "")} onClick={() => setAllowDouble(false)}>
              Not allowed
            </button>
            <button className={"epcr-cn-toggle" + (allowDouble ? " active" : "")} onClick={() => setAllowDouble(true)}>
              Allowed
            </button>
          </div>
          <div className="epcr-cn-opt-hint">
            {allowDouble
              ? "The narrative may restate specific values already captured elsewhere in the report."
              : "CADMUS will avoid duplicating data already captured elsewhere in the report."}
          </div>
        </div>
        <div className="epcr-cn-opt-group">
          <div className="epcr-cn-opt-label">Narrative format</div>
          <div className="epcr-cn-toggle-row">
            {NARRATIVE_FORMATS.map(f => (
              <button
                key={f.key}
                className={"epcr-cn-toggle" + (format === f.key ? " active" : "")}
                onClick={() => setFormat(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="epcr-cn-opt-hint">{NARRATIVE_FORMATS.find(f => f.key === format)?.hint}</div>
        </div>
      </div>

      <div className="epcr-cn-generate-row">
        <button className="epcr-vp-send" onClick={handleGenerate} disabled={generating}>
          {generating ? "Generating…" : (draft ? "Regenerate Draft" : "Generate Draft")}
        </button>
        {genError && <span className="epcr-cn-error">{genError}</span>}
      </div>

      <div className="epcr-cn-draft">
        <div className="epcr-cn-draft-label">Draft narrative{draft && !generating ? " (editable)" : ""}</div>
        <textarea
          className="epcr-cn-draft-text"
          rows={14}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder={generating ? "Generating draft…" : "Select options above, then press Generate Draft."}
          disabled={generating}
        />
      </div>

      {candidatesLoading && (
        <div className="epcr-cn-candidates-status">Checking narrative completeness…</div>
      )}
      {candidatesError && <div className="epcr-cn-error">{candidatesError}</div>}
      {!candidatesLoading && candidates && candidates.length === 0 && (
        <div className="epcr-cn-candidates-status">No additional narrative suggestions — looks complete.</div>
      )}
      {!candidatesLoading && candidates && candidates.length > 0 && (
        <div className="epcr-cn-candidates">
          <div className="epcr-cn-draft-label">Narrative Candidates ({candidates.length})</div>
          {candidates.map(c => {
            const filled = !!(medicTextByNumber[c.suggestionNumber] || '').trim();
            return (
              <div key={c.suggestionNumber} className="epcr-cn-candidate">
                <div className="epcr-cn-candidate-text">{c.feedbackText || c.thenText}</div>
                {c.feedbackJustification && (
                  <div className="epcr-cn-candidate-sub">{c.feedbackJustification}</div>
                )}
                <textarea
                  className={"epcr-cn-candidate-input" + (filled ? " filled" : "")}
                  rows={2}
                  value={medicTextByNumber[c.suggestionNumber] || ''}
                  onChange={e => setMedicTextByNumber(m => ({ ...m, [c.suggestionNumber]: e.target.value }))}
                  placeholder="Add text to address this in the narrative (optional)"
                />
              </div>
            );
          })}
          <div className="epcr-cn-generate-row">
            <button
              className="epcr-vp-send"
              onClick={handleApplyCandidates}
              disabled={applying || !Object.values(medicTextByNumber).some(t => t && t.trim())}
            >
              {applying ? "Applying…" : "Apply to Narrative"}
            </button>
            {applyError && <span className="epcr-cn-error">{applyError}</span>}
          </div>
        </div>
      )}

      {preApplyNarrative != null && preApplyNarrative !== draft && (
        <div className="epcr-cn-diff">
          <div className="epcr-cn-diff-toggle" onClick={() => setShowDiff(s => !s)}>
            {showDiff ? "Hide" : "View"} changes to narrative {showDiff ? "▲" : "▼"}
          </div>
          {showDiff && (
            <ReactDiffViewer
              oldValue={preApplyNarrative}
              newValue={draft}
              splitView={true}
              disableWordDiff={false}
              hideLineNumbers={true}
              hideMarkers={true}
              showDiffOnly={false}
              compareMethod={DiffMethod.WORDS}
            />
          )}
        </div>
      )}

      <div className="epcr-cn-foot">
        <button className="epcr-vp-send" onClick={handleAccept} disabled={!draft.trim()}>
          Accept Narrative
        </button>
      </div>

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}

// Standalone schematron-only popup, triggered by the toolbar's check-circle button (separate
// from the tabbed ValidationPanel, which the rainbow star button opens) — see EpcrApp.
function SchematronPanel({ violations, validating, totalCount, onClose, onRerun, onNavigate, specFn }) {
  const data = useData();
  const fatal   = (violations || []).filter(v => v.role === '[FATAL]');
  const warning = (violations || []).filter(v => v.role !== '[FATAL]');

  return ReactDOM.createPortal(
    <div className="epcr-vp-back" onClick={onClose}>
      <div className="epcr-vp" onClick={e => e.stopPropagation()}>
        <div className="epcr-vp-head">
          {/* Triple left-click toggles debug mode — see the same gesture on ValidationPanel's logo. */}
          <img
            src="/logo-dark.png"
            alt="CADMUS"
            style={{ height: 28, display: "block" }}
            onClick={e => { if (e.detail === 3) data.toggleDebugMode(); }}
          />
          {violations !== null && !validating && (
            <span className={"epcr-vp-count" + (totalCount === 0 ? " ok" : "")}>
              {totalCount === 0 ? "All clear" : `${totalCount} issue${totalCount !== 1 ? 's' : ''}`}
            </span>
          )}
          <div className="epcr-vp-actions">
            <button className="epcr-vp-rerun" onClick={e => { e.stopPropagation(); onRerun(); }} disabled={validating}>
              {validating ? "Running…" : "Re-run"}
            </button>
          </div>
          <button className="epcr-vp-close" onClick={onClose}>
            <Icons.close size={32} />
          </button>
        </div>

        <div className="epcr-vp-list">
          {validating && <div className="epcr-vp-empty">Running schematron validation…</div>}
          {!validating && violations === null && (
            <div className="epcr-vp-empty">Click Re-run to validate this report against the schematron rules.</div>
          )}
          {!validating && violations !== null && totalCount === 0 && (
            <div className="epcr-vp-empty">No violations found.</div>
          )}
          {!validating && fatal.length > 0 && (
            <>
              <div className="epcr-vp-group-head">Fatal ({fatal.length})</div>
              {fatal.map((v, i) => (
                <ViolationItem key={i} v={v} onNavigate={v => { onNavigate(v); onClose(); }} specFn={specFn} />
              ))}
            </>
          )}
          {!validating && warning.length > 0 && (
            <>
              <div className="epcr-vp-group-head">Warning ({warning.length})</div>
              {warning.map((v, i) => (
                <ViolationItem key={i} v={v} onNavigate={v => { onNavigate(v); onClose(); }} specFn={specFn} />
              ))}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function ViolationItem({ v, onNavigate, specFn }) {
  const data = useData();
  const isFatal  = v.role === '[FATAL]';
  const spec     = v.element ? specFn(v.element) : null;
  const elemName = spec?.name || '';

  return (
    <div className="epcr-vp-item-wrap">
      <button className="epcr-vp-item" onClick={() => onNavigate(v)}>
        <div className="epcr-vp-item-meta">
          <span className={"epcr-vp-badge " + (isFatal ? "fatal" : "warn")}>
            {isFatal ? "Fatal" : "Warning"}
          </span>
          {v.element && <span className="epcr-vp-elem">{v.element}</span>}
          {elemName && <span className="epcr-vp-name">{elemName}</span>}
        </div>
        <div className="epcr-vp-msg">{v.message}</div>
      </button>
      {data?.debugMode && v.contextXml && (
        <details className="epcr-vp-ctx">
          <summary>context</summary>
          {v.contextLocalName != null && (
            <div className="epcr-vp-ctx-row">
              <span className="epcr-vp-ctx-label">node:</span>
              <span className="epcr-vp-ctx-val">{v.contextLocalName}</span>
            </div>
          )}
          {v.contextGroupIndex != null && (
            <div className="epcr-vp-ctx-row">
              <span className="epcr-vp-ctx-label">groupIndex:</span>
              <span className="epcr-vp-ctx-val">{v.contextGroupIndex}</span>
            </div>
          )}
          <pre className="epcr-vp-ctx-xml">{v.contextXml}</pre>
        </details>
      )}
    </div>
  );
}

function formatNoteTimestamp(ts) {
  const d = new Date(ts);
  return isNaN(d.getTime()) ? ts : d.toLocaleString();
}

// Formats a Date as a "YYYY-MM-DDTHH:mm:ss" string in local time, the value
// shape <input type="datetime-local" step="1"> expects.
function toDateTimeLocalValue(d) {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// FileReader's readAsDataURL result is "data:<mime>;base64,<data>" — strip the prefix down
// to the raw base64 payload the transcribe endpoints expect.
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// attachedNotes.source is the raw noteKey used for uid uniqueness ("app:<batchId>" for
// app-submitted batches) — resolve it back to something readable for display only. batchId is
// a DB auto-increment integer (not a timestamp), so this just labels which batch, rather than
// trying to derive a time from it — the note's own real timestamp is already shown separately
// on the same row via formatNoteTimestamp(n.timestamp).
function formatAttachedSource(source) {
  if (typeof source === 'string' && source.startsWith('app:')) {
    const batchId = source.slice(4);
    return `App batch #${batchId}`;
  }
  if (typeof source === 'string' && source.startsWith('aws:')) {
    const batchId = source.slice(4);
    // Numeric -> a real DB batch_id; otherwise it's a static demo recording's filename
    // (see handleTranscribeRecording), which reads better without the "#".
    return /^\d+$/.test(batchId) ? `AWS Transcribe batch #${batchId}` : `AWS Transcribe — ${batchId}`;
  }
  return source;
}

// Shared row for one attachable collection of recordings (a static demo file or an
// app-submitted batch) — expand/collapse, "Attach all", and a per-recording checkbox list.
function VoiceNoteSourceRow({ noteKey, label, meta, recordings, isOpen, onToggleOpen, onAttachAll,
                                attachedUidSet, onToggleAttach,
                                hasAudio, onProcessBatch, isProcessing, awsTranscribed }) {
  return (
    <div className="epcr-vp-item-wrap">
      <div className="epcr-vp-item" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer' }} onClick={onToggleOpen}>
        <div className="epcr-vp-item-meta" style={{ flex: 1 }}>
          <span className="epcr-vp-name">{label}</span>
          {meta && <span className="epcr-vp-elem">{meta}</span>}
          {/* Confirms the automatic replace-on-arrival (receiveNotesFromApp →
              autoTranscribeAndReplaceBatch) actually landed for this batch — these are AWS
              Transcribe Medical's own notes, not the phone's on-device transcription anymore. */}
          {awsTranscribed && <span className="epcr-vp-badge aws">AWS Transcribed</span>}
        </div>
        {hasAudio && (
          <button
            className="epcr-mut-btn accept"
            style={{ fontSize: 19, padding: '4px 14px', marginRight: 10, display: 'flex', alignItems: 'center', gap: 6, opacity: isProcessing ? 0.6 : 1 }}
            onClick={e => { e.stopPropagation(); onProcessBatch(); }}
            disabled={isProcessing}
          >
            <Icons.mic size={13} />
            {isProcessing ? 'Processing…' : 'Process with AWS Transcribe Medical'}
          </button>
        )}
        <button
          className="epcr-mut-btn accept"
          style={{ fontSize: 19, padding: '4px 14px', marginRight: 10 }}
          onClick={e => { e.stopPropagation(); onAttachAll(); }}
        >
          Attach all
        </button>
        <span style={{ color: '#9a9a9a', fontSize: 20 }}>{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && (
        <div style={{ padding: '0 36px 14px' }}>
          {!recordings && <div className="epcr-vp-empty" style={{ padding: '20px 0' }}>Loading…</div>}
          {recordings && recordings.length === 0 && (
            <div className="epcr-vp-empty" style={{ padding: '20px 0' }}>No recordings.</div>
          )}
          {recordings && recordings.map(rec => {
            const uid = `${noteKey}:${rec.id}`;
            const checked = attachedUidSet.has(uid);
            return (
              <label
                key={rec.id}
                style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 0', borderTop: '1px solid #f1f1f1', cursor: 'pointer' }}
              >
                <input type="checkbox" checked={checked} onChange={() => onToggleAttach(rec, noteKey)} style={{ marginTop: 4 }} />
                <div>
                  <div className="epcr-vp-elem">{formatNoteTimestamp(rec.timestamp)}</div>
                  <div className="epcr-vp-msg">{rec.text}</div>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VoiceNotesPanel({ files, cache, onLoadFile, attachedNotes, onToggleAttach, onAttachAllFromFile,
                            onAddManual, onRemoveAttached, onClearAttached,
                            appBatches = [], onRefreshAppNotes,
                            onProcessBatch, processingBatchId, processError,
                            onTranscribeRecording, transcribingRecording, recordingTranscribeError }) {
  const data = useData();
  const debugMode = data.debugMode;
  const [expandedKey, setExpandedKey]           = React.useState(null);
  const [manualText, setManualText]             = React.useState('');
  const [manualTimestamp, setManualTimestamp]   = React.useState(() => toDateTimeLocalValue(new Date()));

  const attachedUidSet = React.useMemo(() => new Set(attachedNotes.map(n => n.uid)), [attachedNotes]);

  // This panel only mounts while the Voice Notes sub-tab is active (see ValidationPanel),
  // so mount/unmount is already the right signal for when to poll for app-submitted notes.
  React.useEffect(() => {
    onRefreshAppNotes();
    const id = setInterval(onRefreshAppNotes, 5000);
    return () => clearInterval(id);
  }, [onRefreshAppNotes]);

  const handleExpand = (key) => {
    setExpandedKey(prev => (prev === key ? null : key));
  };

  const handleExpandFile = (file) => {
    const wasOpen = expandedKey === file;
    handleExpand(file);
    if (!wasOpen && !cache[file]) onLoadFile(file);
  };

  const handleAttachAllFile = async (file) => {
    const recordings = cache[file] || (await onLoadFile(file));
    onAttachAllFromFile(file, recordings);
  };

  const handleAdd = () => {
    const text = manualText.trim();
    if (!text) return;
    const iso = manualTimestamp ? new Date(manualTimestamp).toISOString() : new Date().toISOString();
    onAddManual(text, iso);
    setManualText('');
    // manualTimestamp is intentionally left as-is so consecutive notes default
    // to the same time unless the user changes it.
  };

  return (
    <>
      {debugMode && attachedNotes.length > 0 && (
        <>
          <div className="epcr-vp-group-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Attached to this send ({attachedNotes.length})</span>
            <button
              className="epcr-mut-btn reject"
              style={{ padding: '4px 14px', fontSize: 19 }}
              onClick={onClearAttached}
            >
              Remove all
            </button>
          </div>
          {attachedNotes.map(n => (
            <div key={n.uid} className="epcr-vp-item" style={{ cursor: 'default' }}>
              <div className="epcr-vp-item-meta">
                <span className="epcr-vp-elem">{formatNoteTimestamp(n.timestamp)}</span>
                <span className="epcr-vp-name" style={{ fontSize: 20, fontWeight: 500, color: '#9a9a9a' }}>
                  {formatAttachedSource(n.source)}
                </span>
                <button
                  className="epcr-mut-btn reject"
                  style={{ marginLeft: 'auto', padding: '4px 14px', fontSize: 20 }}
                  onClick={() => onRemoveAttached(n.uid)}
                >
                  Remove
                </button>
              </div>
              <div className="epcr-vp-msg">{n.text}</div>
            </div>
          ))}
        </>
      )}

      <div className="epcr-vp-group-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>From app{debugMode && appBatches.length > 0 ? ` (${appBatches.length})` : ''}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="epcr-mut-btn accept"
            style={{ padding: '4px 14px', fontSize: 19 }}
            onClick={onRefreshAppNotes}
          >
            Refresh
          </button>
        </div>
      </div>
      {processError && (
        <div className="epcr-vp-empty" style={{ padding: '4px 36px', color: '#c0392b' }}>{processError}</div>
      )}
      {appBatches.length === 0 && (
        <div className="epcr-vp-empty" style={{ padding: '14px 36px' }}>No notes received from the app yet.</div>
      )}
      {/* Non-debug: only the newest batch (appBatches is already newest-first). */}
      {(debugMode ? appBatches : appBatches.slice(0, 1)).map(batch => {
        const key = `app:${batch.batchId}`;
        return (
          <VoiceNoteSourceRow
            key={key}
            noteKey={key}
            label={`From App — ${formatNoteTimestamp(batch.receivedAt)}`}
            meta={`${batch.count} recording${batch.count !== 1 ? 's' : ''}`}
            recordings={batch.recordings}
            isOpen={expandedKey === key}
            onToggleOpen={() => handleExpand(key)}
            onAttachAll={() => onAttachAllFromFile(key, batch.recordings)}
            attachedUidSet={attachedUidSet}
            onToggleAttach={onToggleAttach}
            // Its output (Transcription Comparison) now lives in the debug-only
            // Debug tab, so don't surface a trigger for it outside debug mode.
            hasAudio={!!batch.audioS3Key && debugMode}
            onProcessBatch={() => onProcessBatch(batch.batchId)}
            isProcessing={processingBatchId === batch.batchId}
            awsTranscribed={batch.transcriptionSource === 'aws'}
          />
        );
      })}

      {debugMode && (
        <>
          <div className="epcr-vp-group-head">Voice note files</div>
          {(files || []).map(file => (
            <VoiceNoteSourceRow
              key={file}
              noteKey={file}
              label={file}
              meta={cache[file] ? `${cache[file].length} recording${cache[file].length !== 1 ? 's' : ''}` : null}
              recordings={cache[file]}
              isOpen={expandedKey === file}
              onToggleOpen={() => handleExpandFile(file)}
              onAttachAll={() => handleAttachAllFile(file)}
              attachedUidSet={attachedUidSet}
              onToggleAttach={onToggleAttach}
            />
          ))}

          <div className="epcr-vp-group-head">Recorded audio</div>
          {recordingTranscribeError && (
            <div className="epcr-vp-empty" style={{ padding: '4px 36px', color: '#c0392b' }}>{recordingTranscribeError}</div>
          )}
          {VOICE_NOTE_RECORDINGS.map(filename => (
            // Same wrapper/row/meta structure as VoiceNoteSourceRow's own header (label flex:1,
            // action button right-aligned after it) — kept identical in both the idle and
            // transcribing states, on purpose: transcribing only swaps the button's label/disabled
            // state, never the layout, so the button stays put instead of jumping around. Results
            // open directly in the Debug tab's Transcription Comparison rather than expanding here.
            <div key={filename} className="epcr-vp-item-wrap">
              <div className="epcr-vp-item" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <div className="epcr-vp-item-meta" style={{ flex: 1 }}>
                  <span className="epcr-vp-name">{filename}</span>
                </div>
                <button
                  className="epcr-mut-btn accept"
                  style={{ fontSize: 19, padding: '4px 14px', opacity: transcribingRecording === filename ? 0.6 : 1 }}
                  onClick={() => onTranscribeRecording(filename)}
                  disabled={transcribingRecording === filename}
                >
                  {transcribingRecording === filename ? 'Transcribing…' : 'Send to be transcribed'}
                </button>
              </div>
            </div>
          ))}

          <div className="epcr-vp-group-head">New voice note (this session only)</div>
          <div style={{ padding: '14px 36px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="epcr-vp-msg" style={{ color: '#9a9a9a', fontSize: 20 }}>
              Not saved to any file — only attached for this browser session.
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, alignSelf: 'flex-start' }}>
              <span style={{ fontSize: 19, color: '#9a9a9a' }}>Timestamp</span>
              <input
                type="datetime-local"
                step="1"
                value={manualTimestamp}
                onChange={e => setManualTimestamp(e.target.value)}
                style={{ fontFamily: 'inherit', fontSize: 20, padding: 8, border: '1.5px solid #d4d4d4', borderRadius: 6 }}
              />
            </label>
            <textarea
              value={manualText}
              onChange={e => setManualText(e.target.value)}
              placeholder="Transcribed voice note text…"
              rows={3}
              style={{ width: '100%', fontFamily: 'inherit', fontSize: 22, padding: 10, boxSizing: 'border-box', border: '1.5px solid #d4d4d4', borderRadius: 6, resize: 'vertical' }}
            />
            <button
              className="epcr-mut-btn accept"
              style={{ alignSelf: 'flex-start', opacity: manualText.trim() ? 1 : 0.5 }}
              onClick={handleAdd}
              disabled={!manualText.trim()}
            >
              Add note
            </button>
          </div>
        </>
      )}
    </>
  );
}

// Shows the result of the most recent "Process with AWS Transcribe Medical" click — the
// phone's own on-device transcription side by side with AWS's, so a person can eyeball whether
// they agree. No automated diff/alignment: the ask is manual verification, and the two sides
// can legitimately have different note boundaries (different VAD/segmentation entirely) even
// when the underlying speech content matches.
function TranscriptionComparisonPanel({ comparison, onAttachAllFromFile }) {
  const [audioUrl, setAudioUrl]         = React.useState(null);
  const [audioLoading, setAudioLoading] = React.useState(false);
  const [audioError, setAudioError]     = React.useState(null);
  const batchId = comparison?.batchId;

  // A new "Process" click can swap in a different batch while this tab is still mounted —
  // don't leave a stale player pointed at the previous batch's audio.
  React.useEffect(() => {
    setAudioUrl(null);
    setAudioError(null);
  }, [batchId]);

  const handleLoadAudio = async () => {
    setAudioLoading(true);
    setAudioError(null);
    try {
      const resp = await fetch(URLS.GET_BATCH_AUDIO_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchId }),
      });
      const result = await resp.json();
      if (result.status === 'success') {
        setAudioUrl(result.url);
      } else {
        setAudioError(result.message || 'Could not load audio.');
      }
    } catch (e) {
      console.error('Get batch audio url error:', e);
      setAudioError('Audio request failed.');
    } finally {
      setAudioLoading(false);
    }
  };

  if (!comparison) {
    return (
      <div className="epcr-vp-empty" style={{ padding: '20px 36px' }}>
        Nothing transcribed yet — click "Process with AWS Transcribe Medical" on a "From app"
        batch, or "Send to be transcribed" on a recorded audio clip, in the Voice Notes tab.
      </div>
    );
  }

  const { phoneNotes = [], awsNotes = [], audioTruncated, audioAvailable = true } = comparison;
  // Phone batches use a numeric DB batch_id; the static demo recordings use their filename as a
  // stand-in id (see handleTranscribeRecording) — label accordingly rather than showing
  // "Batch #cardiac_arrest_bodycam.mp3".
  const sourceLabel = /^\d+$/.test(String(batchId)) ? `Batch #${batchId}` : String(batchId);
  const sortedPhone = [...phoneNotes].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const sortedAws = [...awsNotes].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  // Raw view: same notes, sorted by AWS's own file-relative seconds rather than the
  // clip-corrected wall-clock timestamp — each note is one of AWS's own speaker turns (see
  // segmentIntoTimestampedNotes), so this is AWS's raw output, just re-sorted.
  const sortedRaw = [...awsNotes].sort((a, b) => a.rawStartSec - b.rawStartSec);

  return (
    <div className="epcr-vp-comparison" style={{ padding: '14px 36px' }}>
      <div className="epcr-vp-msg" style={{ color: '#9a9a9a', marginBottom: 6 }}>
        {sourceLabel} — {phoneNotes.length} note{phoneNotes.length !== 1 ? 's' : ''} from phone,
        {' '}{awsNotes.length} from AWS. Note boundaries may not match exactly between phone and
        AWS — compare content, not count.
      </div>
      {audioAvailable && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          {!audioUrl && (
            <button className="epcr-mut-btn accept" onClick={handleLoadAudio} disabled={audioLoading}>
              {audioLoading ? 'Loading audio…' : 'Play audio'}
            </button>
          )}
          {audioUrl && <audio controls autoPlay src={audioUrl} style={{ height: 30 }} />}
          {audioError && <span style={{ color: '#c0392b' }}>{audioError}</span>}
        </div>
      )}
      {audioTruncated && (
        <div className="epcr-vp-msg" style={{ color: '#c0392b', marginBottom: 10 }}>
          This session's audio was truncated by the phone's 30-minute retention cap — corrected
          timestamps near the very start of the audio may be approximate.
        </div>
      )}
      <div style={{ display: 'flex', gap: 20 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="epcr-vp-group-head">From phone ({sortedPhone.length})</div>
          {sortedPhone.length === 0 && <div className="epcr-vp-empty">No phone notes.</div>}
          {sortedPhone.map((n, i) => (
            <div key={i} className="epcr-vp-item" style={{ cursor: 'default' }}>
              <div className="epcr-vp-item-meta">
                <span className="epcr-vp-elem">{formatNoteTimestamp(n.timestamp)}</span>
              </div>
              <div className="epcr-vp-msg">{n.text}</div>
            </div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="epcr-vp-group-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>From AWS Transcribe Medical ({sortedAws.length})</span>
            <button
              className="epcr-mut-btn accept"
              style={{ opacity: sortedAws.length ? 1 : 0.5 }}
              onClick={() => onAttachAllFromFile(`aws:${batchId}`, awsNotes)}
              disabled={!sortedAws.length}
            >
              Attach all to this send
            </button>
          </div>
          {sortedAws.length === 0 && <div className="epcr-vp-empty">No AWS notes.</div>}
          {sortedAws.map((n) => (
            <div key={n.id} className="epcr-vp-item" style={{ cursor: 'default' }}>
              <div className="epcr-vp-item-meta">
                <span className="epcr-vp-elem">{formatNoteTimestamp(n.timestamp)}</span>
                <span className="epcr-vp-name" style={{ color: '#9a9a9a' }}>
                  {n.speaker || 'Unknown speaker'}
                  {n.confidence != null && ` · ${Math.round(n.confidence * 100)}% confidence`}
                </span>
              </div>
              <div className="epcr-vp-msg">{n.text}</div>
            </div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="epcr-vp-group-head">Raw AWS output, by speaker ({sortedRaw.length})</div>
          {sortedRaw.length === 0 && <div className="epcr-vp-empty">No AWS output.</div>}
          {sortedRaw.map((n) => (
            <div key={n.id} className="epcr-vp-item" style={{ cursor: 'default' }}>
              <div className="epcr-vp-item-meta">
                <span className="epcr-vp-elem">
                  {n.rawStartSec != null ? n.rawStartSec.toFixed(2) : '?'}s
                  {' – '}
                  {n.rawEndSec != null ? n.rawEndSec.toFixed(2) : '?'}s
                </span>
                <span className="epcr-vp-name" style={{ color: '#9a9a9a' }}>
                  {n.speaker || 'Unknown speaker'}
                </span>
              </div>
              <div className="epcr-vp-msg">{n.text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const FEEDBACK_TARGET_LABELS = {
  'missing-vital': 'Missing Vital',
  'missing-med': 'Missing Medication',
  'missing-procedure': 'Missing Procedure',
  'missing-allergy': 'Missing Allergy',
  'missing-history': 'Missing History',
  'vitals-discrepancy': 'Vitals Discrepancy',
  'medications-discrepancy': 'Medications Discrepancy',
  'procedure-discrepancy': 'Procedure Discrepancy',
  'schematron': 'Schematron',
};

function DropdownResultItem({ r }) {
  return (
    <div className="epcr-vp-item" style={{ cursor: 'default' }}>
      <div className="epcr-vp-item-meta">
        <span className="epcr-vp-badge warn">
          {FEEDBACK_TARGET_LABELS[r.feedbackTarget] || r.feedbackTarget}
        </span>
        {r.validated && (
          <span className="epcr-vp-badge" style={{ background: '#eef9f0', color: '#16a34a' }}>Validated</span>
        )}
        {r.test_metadata_id && (
          <span className="epcr-vp-elem" style={{ marginLeft: 'auto' }}>#{r.test_metadata_id}</span>
        )}
      </div>
      <div className="epcr-vp-msg" style={{ fontWeight: 500 }}>{r.feedbackText}</div>
      {r.feedbackJustification && (
        <div className="epcr-vp-msg" style={{ color: '#6e6e6e', fontSize: '22px' }}>{r.feedbackJustification}</div>
      )}
    </div>
  );
}

// Mirrors the backend's GROUP_IDENTITY (gpt-controller.js) — the repeating group's
// catalog-backed identifying field, used to resolve a human-readable name for the
// specific instance a mutation targets (e.g. "Medication #2 — Oral Glucose" instead
// of a bare index).
const GROUP_IDENTITY_ELEM = {
  'eMedications.MedicationGroup': 'eMedications.03',
  'eProcedures.ProcedureGroup':   'eProcedures.03',
};

const OP_META = {
  add_group:      { label: 'Add Group',   bg: 'var(--epcr-blue-soft)', color: 'var(--epcr-blue)' },
  add_group_full: { label: 'Full Add',    bg: 'var(--epcr-blue-soft)', color: 'var(--epcr-blue)' },
  set:            { label: 'Set',         bg: 'var(--epcr-blue-soft)', color: 'var(--epcr-blue)' },
  replace:        { label: 'Replace',     bg: '#fff8e1',               color: '#8a6f00' },
  clear:          { label: 'Clear',       bg: 'var(--epcr-red-soft)',  color: 'var(--epcr-red)' },
  ask_user:       { label: 'Needs Input', bg: 'var(--epcr-blue-soft)', color: 'var(--epcr-blue)' },
};

function MutationCard({ r, isAccepted, onAccept, onReject }) {
  const data = useData();
  const op = OP_META[r.op] || null;
  const isActionable = r.op !== null;
  const isFatal = r.severity === '[FATAL]';
  const isAskUser = r.op === 'ask_user';
  const isRichAdd = r.op === 'add_group_full';

  // Local-only draft values for a rich add's optional fill-in fields — there's no
  // report path to bind to yet (the group doesn't exist until Accept), unlike
  // ask_user's live-path fields below. Merged into r.template only on Accept.
  const [draftFields, setDraftFields] = React.useState({});

  // Resolved unconditionally (cheap, side-effect free) so the useField() hook below
  // always runs in the same order regardless of op/accepted state.
  const m = promoteToMutation(r, data);
  const hasGroupIndex = Number.isInteger(r.groupIndex) && r.groupIndex >= 0;
  const askUserPath = isAskUser ? (m.path || '') : '';
  const askUserValue = useField(askUserPath);
  const hasAskUserValue = Array.isArray(askUserValue)
    ? askUserValue.length > 0
    : (askUserValue !== null && askUserValue !== undefined && askUserValue !== '');

  // Identify which member of a repeating group this card targets — e.g. "Medication
  // #2 — Oral Glucose" — so the user (and, separately, the LLM in enrichSchematron-
  // Violations) never has to guess which instance is actually being discussed.
  const groupNumber = m.groupPath ? m.groupPath.split('/').pop() : null;
  const idElem = groupNumber ? GROUP_IDENTITY_ELEM[groupNumber] : null;
  const idPath = (hasGroupIndex && idElem)
    ? (promoteToMutation({ elem: idElem, groupIndex: r.groupIndex }, data).path || '')
    : '';
  const idValue = useField(idPath);
  const idLabel = (idElem && idValue != null && idValue !== '') ? data.label(idElem, idValue) : null;
  const groupHeaderLabel = groupNumber ? groupLabel(groupNumber) : null;

  // Reject on an ask_user card must leave no trace in the report, matching every
  // other card type — if the user had started answering, clear it back out first.
  const handleRejectAskUser = () => {
    if (hasAskUserValue && askUserPath) {
      data.setReportPath(askUserPath, Array.isArray(askUserValue) ? [] : "");
    }
    onReject(r);
  };

  // Only merge draft values the user actually filled in — a field opened and then
  // cleared back out should end up absent, same as never having been touched at all.
  const mergedRichAddTemplate = () => ({
    ...r.template,
    ...Object.fromEntries(
      Object.entries(draftFields).filter(([, v]) => v !== '' && v != null && !(Array.isArray(v) && v.length === 0))
    ),
  });

  // Human-readable display for a rich add's known-fields preview — resolves coded
  // values (including multi-select arrays) via data.label(), and lightly formats
  // dateTime values instead of showing the full ISO-8601 offset string.
  const formatKnownValue = (elem, value) => {
    if (Array.isArray(value)) return value.map(v => data.label(elem, v)).join(', ');
    const spec = data.spec(elem);
    if ((spec?.baseType === 'dateTime' || spec?.baseType === 'date') && typeof value === 'string') {
      return value.substring(0, 16).replace('T', ' ');
    }
    return data.label(elem, value);
  };

  if (isAccepted) {
    return (
      <div className="epcr-mut-accepted-row">
        <span className="epcr-mut-check">✓</span>
        <span className="epcr-mut-accepted-label">
          {data?.debugMode ? (r.display || r.feedbackText) : (r.feedbackText || r.display)}
        </span>
        {data?.debugMode && r.elem && <span className="epcr-vp-elem">{r.elem}</span>}
      </div>
    );
  }

  return (
    <div className="epcr-vp-item epcr-mut-card" style={{ cursor: 'default' }}>
      <div className="epcr-vp-item-meta">
        {op && (
          <span className="epcr-vp-badge" style={{ background: op.bg, color: op.color }}>
            {op.label}
          </span>
        )}
        <span className="epcr-vp-badge warn">
          {FEEDBACK_TARGET_LABELS[r.feedbackTarget] || r.feedbackTarget}
        </span>
        {r.severity && (
          <span className={"epcr-vp-badge " + (isFatal ? 'fatal' : 'warn')}>
            {isFatal ? 'Fatal' : 'Warning'}
          </span>
        )}
        {r.test_metadata_id && (
          <span className="epcr-vp-elem" style={{ marginLeft: 'auto' }}>#{r.test_metadata_id}</span>
        )}
      </div>

      {data?.debugMode && r.display && (
        <div className="epcr-vp-msg" style={{ fontWeight: 600, fontSize: '26px' }}>{r.display}</div>
      )}
      {r.feedbackText && (
        <div className="epcr-vp-msg" style={{ fontWeight: 500 }}>{r.feedbackText}</div>
      )}
      {r.feedbackJustification && (
        <div className="epcr-vp-msg" style={{ color: '#6e6e6e', fontSize: '22px' }}>{r.feedbackJustification}</div>
      )}

      {hasGroupIndex && groupHeaderLabel && (
        <div className="epcr-mut-group-tag">
          {groupHeaderLabel} #{r.groupIndex + 1}{idLabel ? ` — ${idLabel}` : ''}
        </div>
      )}

      {isAskUser ? (
        <div className="epcr-mut-ask-field epcr-root">
          <RawField elem={r.elem} path={hasGroupIndex ? m.path : undefined} />
        </div>
      ) : isRichAdd ? (
        <>
          <div className="epcr-mut-fields">
            {Object.entries(r.template || {}).map(([elem, val]) => (
              <span className="epcr-mut-field" key={elem}>
                <span className="epcr-mut-field-label">{data.spec(elem)?.name || elem}</span>
                <span className="epcr-vp-elem">{formatKnownValue(elem, val)}</span>
              </span>
            ))}
          </div>
          {r.missingFields && r.missingFields.length > 0 && (
            <>
              <div className="epcr-mut-field-label" style={{ marginTop: 8 }}>
                Optional — fill in now, or answer later
              </div>
              <div className="epcr-mut-ask-field epcr-root">
                {r.missingFields.map(elem => (
                  <AutoField
                    key={elem}
                    elem={elem}
                    label={data.spec(elem)?.name || elem}
                    value={draftFields[elem]}
                    onChange={v => setDraftFields(f => ({ ...f, [elem]: v }))}
                  />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="epcr-mut-fields">
          {/* Outside debug mode, show what these actually mean rather than raw NEMSIS
              codes — the element's spec name, and the value resolved through the same
              code→label lookup (with array/date formatting) the rich-add preview above
              uses. formatKnownValue/data.spec both fall back to the raw code when there's
              no human-readable form (e.g. free text), so nothing goes blank. */}
          {r.elem && (
            <span className="epcr-mut-field">
              <span className="epcr-mut-field-label">elem</span>
              <span className="epcr-vp-elem">
                {data?.debugMode ? r.elem : (data.spec(r.elem)?.name || r.elem)}
              </span>
            </span>
          )}
          {r.value !== null && r.value !== undefined && (
            <span className="epcr-mut-field">
              <span className="epcr-mut-field-label">value</span>
              <span className="epcr-vp-elem">
                {data?.debugMode ? String(r.value) : formatKnownValue(r.elem, r.value)}
              </span>
            </span>
          )}
        </div>
      )}

      {isActionable ? (
        <div className="epcr-mut-actions">
          <button
            className={"epcr-mut-btn accept" + (isFatal ? " fatal-accept" : "")}
            onClick={() => onAccept(
              isRichAdd ? { ...r, template: mergedRichAddTemplate() } : r
            )}
            disabled={isAskUser && !hasAskUserValue}
          >
            Accept
          </button>
          <button
            className="epcr-mut-btn reject"
            onClick={isAskUser ? handleRejectAskUser : () => onReject(r)}
          >
            Reject
          </button>
        </div>
      ) : (
        <div className="epcr-mut-manual">Requires manual review</div>
      )}
    </div>
  );
}
